import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { SettingsTabs } from "@/routes/settings/SettingsTabs";
import {
  artistEditTabPath,
  getArtistEditTabs,
  getCurrentArtistEditTab,
} from "./artistEditTabsShared";

export function ArtistEditRoute() {
  const { artistId } = useParams<{ artistId: string }>();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const navigate = useNavigate();
  const location = useLocation();
  const [artistName, setArtistName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const currentTab = getCurrentArtistEditTab(location.pathname);

  useEffect(() => {
    if (!artistId) return;

    async function fetchArtist() {
      try {
        const response = await fetch(`/api/admin/artists/${artistId}`);
        if (response.ok) {
          const data = await response.json();
          setArtistName(data.artist?.name || "Artist");
        } else {
          setArtistName("Artist");
        }
      } catch (error) {
        console.error("Failed to fetch artist:", error);
        setArtistName("Artist");
      } finally {
        setIsLoading(false);
      }
    }

    fetchArtist();
  }, [artistId]);

  useEffect(() => {
    if (!artistId || !artistName) return;

    setBreadcrumbs([
      { label: "Admin", path: "/admin" },
      { label: "Artists", path: "/admin/artists" },
      { label: artistName, path: artistEditTabPath({ artistId, tab: currentTab }) },
    ]);

    return () => {
      clearBreadcrumbs();
    };
  }, [artistId, artistName, currentTab]);

  const handleTabChange = (value: string) => {
    if (!artistId) return;
    navigate(artistEditTabPath({ artistId, tab: value }));
  };

  if (!artistId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <SettingsTabs
      value={currentTab}
      tabs={getArtistEditTabs({ artistId })}
      onValueChange={handleTabChange}
    />
  );
}
